import React, { useMemo } from "react";
import { SectionList } from "react-native";
import { Container, VDBText } from "@components/atoms";
import TextSearchRecentItem from "@components/atoms/TextSearch/TextSearchRecentItem";
import { TextSearchData } from "@src/types/textSearchResult";
import { Section } from "./types";
import RenderSectionHeader from "./TextSearchHeader";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import LineViewContainer from "@scenes/requests/utils/LineView";
import styled from "styled-components/native";
import { DefaultTheme } from "styled-components/native";
import useScreenOptionsHooks from "../../hooks/useScreenOptionsHooks";

const SHOW_ALL_ITEM_ID = -1;
const MAX_VISIBLE_SHORT_TITLE_RESULTS = 5;

type SearchResultListItem = TextSearchData & { isShowAll?: boolean };

interface SearchResultsProps {
  data: Section[];
  showResultsAsTooltip?: boolean;
  dataCount: number;
  maxRecords: number;
  inputValue: string;
  setInputValue: (value: string) => void;
  onSelectItem: (item: TextSearchData) => void;
  isCustomApp: boolean;
  onSelectShowAll: (item: Section) => void;
  showAll: boolean;
  enableShortTitleFilterInTextSearch?: boolean;
  focussedItem: number;
  setFocusedItem: React.Dispatch<React.SetStateAction<number>>;
}

export const SearchResults = ({
  data,
  showResultsAsTooltip,
  dataCount,
  maxRecords,
  inputValue,
  setInputValue,
  onSelectItem,
  isCustomApp,
  onSelectShowAll,
  enableShortTitleFilterInTextSearch = false,
  focussedItem,
  setFocusedItem,
}: SearchResultsProps) => {
  const { theme, typography } = useScreenOptionsHooks();

  const sections = useMemo(() => {
    if (!enableShortTitleFilterInTextSearch) return data;

    return data.map((section) => {
      if (!section.showAll) return section;

      const visibleItems = section.data.slice(
        0,
        MAX_VISIBLE_SHORT_TITLE_RESULTS
      );
      const showAllItem: SearchResultListItem = {
        id: SHOW_ALL_ITEM_ID,
        isShowAll: true,
      };

      return {
        ...section,
        data: [...visibleItems, showAllItem],
      };
    });
  }, [data, enableShortTitleFilterInTextSearch]);

  const keyExtractor = (item: unknown, index: number) => {
    if (typeof item === "object" && item !== null && "id" in item) {
      const sectionIdentifier =
        (item as any).sectionTitle || (item as any).type || "";
      const uniqueId = (item as any).id || index;
      return `${sectionIdentifier}-${uniqueId}-${index}`;
    }
    return `${index}-item`;
  };

  const getItemLabel = (item: TextSearchData) => {
    return `${item.short_title || ""}${
      item.short_title && item.label ? ", " : ""
    }${
      isCustomApp || (item.label && item.label.includes("VDB-"))
        ? item.label || ""
        : "VDB-" + (item.label || "")
    }`;
  };

  const getSectionData = (section: { title: string }) =>
    data.find((s) => s.title === section.title);

  const ShowAllRow = ({
    section,
    isFocused,
    index,
  }: {
    section: Section;
    isFocused: boolean;
    index: number;
  }) => {
    const sectionData = getSectionData(section);
    return (
      <Container
        noPadding={showResultsAsTooltip}
        {...(theme.isTablet && { applyMaxWidth: false })}
      >
        <StyledPressable
          isFocused={isFocused}
          onPress={() => onSelectShowAll(section)}
          onMouseEnter={() => setFocusedItem(index)}
        >
          <TextSearchRecentItem
            value={sectionData?.message ?? ""}
            type={typography["Body-M"]}
            description={sectionData?.description}
            isFocused={isFocused}
            isShowAll={true}
          />
        </StyledPressable>
      </Container>
    );
  };

  const ArrowItemRow = ({
    item,
    isFocused,
    index,
  }: {
    item: TextSearchData;
    isFocused: boolean;
    index: number;
  }) => (
    <>
      <StyledPressable
        isFocused={isFocused}
        onPress={() => onSelectItem(item)}
        onMouseEnter={() => setFocusedItem(index)}
        style={{
          paddingVertical: 16,
          padding: 16,
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <VDBText
          color={isFocused ? theme["link-hover"] : theme["core-05"]}
          type={typography["Body-R"]}
        >
          {getItemLabel(item)}
        </VDBText>
        <VDBIcon
          color={isFocused ? theme["core-06"] : theme["core-04"]}
          size={SpaceType.Big}
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.ARROW_RIGHT}
        />
      </StyledPressable>
      <LineViewContainer
        background={isFocused ? "core-04" : "core-03"}
        marginLeft
        marginRight
      />
    </>
  );

  const SimpleItemRow = ({
    item,
    isFocused,
    index,
  }: {
    item: TextSearchData;
    isFocused: boolean;
    index: number;
  }) => (
    <Container
      noPadding={showResultsAsTooltip}
      {...(theme.isTablet && { applyMaxWidth: false })}
    >
      <StyledPressable
        isFocused={isFocused}
        onPress={() => onSelectItem(item)}
        onMouseEnter={() => setFocusedItem(index)}
      >
        <TextSearchRecentItem
          value={getItemLabel(item)}
          isFocused={isFocused}
        />
      </StyledPressable>
    </Container>
  );

  const getVisibleCount = (section: Section) => {
    if (section.showAll) {
      if (enableShortTitleFilterInTextSearch) {
        return (
          Math.min(MAX_VISIBLE_SHORT_TITLE_RESULTS, section.data.length) + 1
        );
      }
      return 1;
    }
    if (dataCount >= maxRecords) return 0;
    return section.data.length;
  };

  const getFlatIndex = (sectionIndex: number, itemIndex: number) => {
    let offset = 0;
    for (let i = 0; i < sectionIndex; i++) {
      offset += getVisibleCount(data[i]);
    }
    return offset + itemIndex;
  };

  const renderItem = ({ index, item, section }: any) => {
    const sectionIndex = data.findIndex((s) => s.title === section.title);
    const flatIndex = getFlatIndex(sectionIndex, index);
    const isFocused = focussedItem === flatIndex;

    if (section.showAll) {
      if (enableShortTitleFilterInTextSearch) {
        if ((item as SearchResultListItem)?.isShowAll) {
          return (
            <ShowAllRow
              section={section}
              isFocused={isFocused}
              index={flatIndex}
            />
          );
        }
        return (
          <ArrowItemRow item={item} isFocused={isFocused} index={flatIndex} />
        );
      }

      if (index === 0)
        return (
          <ShowAllRow
            section={section}
            isFocused={isFocused}
            index={flatIndex}
          />
        );
      return null;
    }

    if (dataCount >= maxRecords) return null;

    return (
      <SimpleItemRow item={item} isFocused={isFocused} index={flatIndex} />
    );
  };

  return (
    <SectionList
      sections={sections}
      style={{ backgroundColor: theme["core-02"] }}
      keyExtractor={keyExtractor}
      stickySectionHeadersEnabled={true}
      renderSectionHeader={({ section: { title, data: headerData } }) => (
        <RenderSectionHeader
          title={title}
          headerData={headerData}
          showResultsAsTooltip={showResultsAsTooltip}
          dataCount={dataCount}
          NUMBER_OF_RECORDS_TO_AGGREGATE={maxRecords}
          inputValue={inputValue}
          setInputValue={setInputValue}
        />
      )}
      renderItem={renderItem}
    />
  );
};

const StyledPressable = styled.Pressable<{ isFocused: boolean }>`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-02"]};
`;
